import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const STATUSES = ["Pending", "More info needed", "Approved", "Declined", "All"];

export const statusStyle = (s) =>
  ({
    Pending: "bg-amber-50 text-amber-700 border-amber-200",
    "More info needed": "bg-blue-50 text-blue-700 border-blue-200",
    Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Declined: "bg-red-50 text-red-700 border-red-200",
  }[s] || "bg-slate-50 text-slate-700 border-slate-200");

export default function Applications() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("Pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/applications?status=${encodeURIComponent(status)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || "Could not load applications");
        if (!cancelled) setItems(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) =>
      [a.legalName, a.tradingAs, a.reference, a.contacts?.purchasing?.name, a.contacts?.purchasing?.email]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Customers</h1>
        <p className="text-sm text-slate-500">Livestock shipping account applications submitted from the public form.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <Link to="/dashboard/customers" className="-mb-px border-b-2 border-transparent px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-900">
          Customers
        </Link>
        <span className="-mb-px border-b-2 border-slate-900 px-4 py-2 text-sm font-semibold text-slate-900">Applications</span>
      </div>

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button key={s} type="button" onClick={() => setStatus(s)}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${status === s ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                {s}
              </button>
            ))}
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search business, contact or reference…"
            className="ml-auto w-full rounded-xl border border-slate-200 px-3 py-2 text-sm sm:w-72" />
        </div>

        {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-4">Business</th>
                <th className="py-2 pr-4">Reference</th>
                <th className="py-2 pr-4">Contact</th>
                <th className="py-2 pr-4 text-right">Credit requested</th>
                <th className="py-2 pr-4">Submitted</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">No applications.</td></tr>
              ) : (
                filtered.map((a) => (
                  <tr key={a._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 pr-4">
                      <Link to={`/dashboard/applications/${a._id}`} className="font-semibold text-slate-900 hover:underline">{a.legalName}</Link>
                      {a.tradingAs && <div className="text-xs text-slate-500">t/a {a.tradingAs}</div>}
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-slate-600">{a.reference}</td>
                    <td className="py-3 pr-4">{a.contacts?.purchasing?.name}<div className="text-xs text-slate-500">{a.contacts?.purchasing?.email}</div></td>
                    <td className="py-3 pr-4 text-right">${Number(a.creditLimitRequested || 0).toLocaleString()}</td>
                    <td className="py-3 pr-4 text-slate-600">{new Date(a.createdAt).toLocaleDateString("en-NZ")}</td>
                    <td className="py-3"><span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusStyle(a.status)}`}>{a.status}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}